import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import { storageGet } from '@/lib/nativeStorage';
import { AuthBrandHeader } from '@/layouts/AuthPageLayout';
import { t } from '@/i18n';

type VerifyState = 'loading' | 'success' | 'error' | 'expired';

export function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const token = params.get('token');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('No verification token provided.');
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/verify-email/${token}`);
        const data = (await res.json()) as {
          success?: boolean;
          message?: string;
          error?: string | { code?: string; message?: string };
        };

        if (res.ok && data.success) {
          setState('success');
          return;
        }
        // The API envelope carries the reason under error.message; reading
        // `error` as a string threw here and every refusal — including a
        // link that had simply expired — read "Could not connect to server".
        const reason = typeof data.error === 'string' ? data.error : data.error?.message ?? data.message ?? '';
        if (/expired/i.test(reason)) {
          setState('expired');
          setErrorMsg('This verification link has expired or was already used. If you already verified, just sign in.');
        } else {
          setState('error');
          setErrorMsg(reason || 'Verification failed.');
        }
      } catch {
        setState('error');
        setErrorMsg('Could not connect to server. Please try again.');
      }
    };

    verify();
  }, [token]);

  const handleResend = async () => {
    const authToken = storageGet('buzzbit_token');
    if (!authToken) {
      // Resending needs a session; silently doing nothing left the button dead.
      setErrorMsg('Sign in first, then resend the verification email from your account.');
      return;
    }
    setResending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });
      if (res.ok) {
        setResent(true);
      } else {
        // A refused resend used to leave the button looking as if nothing
        // happened; say why (e.g. "Email is already verified") instead.
        const body = (await res.json().catch(() => ({}))) as { error?: string | { message?: string } };
        const reason = typeof body.error === 'string' ? body.error : body.error?.message;
        setErrorMsg(reason || t('emailVerification.resendFailed'));
      }
    } catch {
      setErrorMsg(t('emailVerification.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-900">
      <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 max-w-md w-full mx-4 text-center">
        <div className="mb-6 rounded-lg bg-white px-3 py-2">
          <AuthBrandHeader className="mb-0" />
        </div>
        {state === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-gray-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-white mb-2">Verifying your email...</h1>
            <p className="text-slate-400">Please wait a moment.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-emerald-400" />
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">Email Verified!</h1>
            <p className="text-slate-400 mb-6">Your email has been verified successfully. You're all set.</p>
            <button
              onClick={() => navigate('/business')}
              className="w-full px-6 py-3 bg-gray-900 hover:bg-gray-900 text-white rounded-xl font-medium transition-colors"
            >
              Go to Business Hub
            </button>
          </>
        )}

        {(state === 'error' || state === 'expired') && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">
              {state === 'expired' ? 'Link Expired' : 'Verification Failed'}
            </h1>
            <p className="text-slate-400 mb-6">{errorMsg}</p>

            {!resent ? (
              <button
                onClick={handleResend}
                disabled={resending}
                className="w-full px-6 py-3 bg-gray-900 hover:bg-gray-900 disabled:bg-gray-900 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </button>
            ) : (
              <p className="text-emerald-400 text-sm">Verification email sent! Check your inbox.</p>
            )}

            <button
              onClick={() => navigate('/login')}
              className="w-full mt-3 px-6 py-3 text-slate-400 hover:text-white transition-colors text-sm"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
